import { cloneElement, isValidElement } from 'react';
import { getErrorCopy } from '@/shared/copy/errors';
import { Box, Surface, Text } from '@/ui/primitives';
import { s } from '../../theme/tokens';
import { Inline } from '../layout/Inline';
import {
  DataGridEmptyState,
  DataGridLoadingState,
  DATA_GRID_EMPTY_STATE_NO_DATA_LABEL,
} from '../primitives/DataGrid';
import type { ReactElement, ReactNode } from 'react';

type TableStateShellProps = {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  state?: 'ready' | 'loading' | 'empty' | 'error';
  loadingRowCount?: number;
  emptyTitle?: string;
  emptyBody?: string;
  errorTitle?: string;
  errorBody?: string;
  errorAction?: ReactNode;
  children?: ReactNode;
};

/**
 * Placeholder/state frame for table-adjacent demos and fallback scaffolds.
 * Production dense table behaviour belongs to TableV2.
 */
export const TableStateShell = ({
  title,
  subtitle,
  actions,
  state = 'ready',
  loadingRowCount,
  emptyTitle = DATA_GRID_EMPTY_STATE_NO_DATA_LABEL,
  emptyBody,
  errorTitle = getErrorCopy('grid.errorTitle'),
  errorBody = getErrorCopy('grid.errorBody'),
  errorAction,
  children,
}: TableStateShellProps): ReactElement => {
  const showHeader = Boolean(title) || Boolean(subtitle) || Boolean(actions);
  const content =
    state === 'loading' && isValidElement(children)
      ? cloneElement(children as ReactElement<{ loading?: boolean }>, { loading: true })
      : children;

  return (
    <Surface
      padding="none"
      sx={{ paddingY: s('sm'), paddingX: s('none'), minWidth: 0, width: '100%' }}
    >
      <Box sx={{ display: 'grid', gap: s('sm'), minWidth: 0, width: '100%' }}>
        {showHeader ? (
          <Inline sx={{ justifyContent: 'space-between' }}>
            {title || subtitle ? (
              <Box sx={{ display: 'grid', gap: s('xxs'), minWidth: 0 }}>
                {title ? <Text role="sectionTitle">{title}</Text> : null}
                {subtitle ? <Text role="bodyMuted">{subtitle}</Text> : null}
              </Box>
            ) : null}
            {actions ? (
              <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
                {actions}
              </Box>
            ) : null}
          </Inline>
        ) : null}
        <Box sx={{ minWidth: 0 }}>
          {state === 'loading' && !isValidElement(children) ? (
            <DataGridLoadingState rowCount={loadingRowCount} />
          ) : null}
          {state === 'empty' ? <DataGridEmptyState title={emptyTitle} body={emptyBody} /> : null}
          {state === 'error' ? (
            <DataGridEmptyState title={errorTitle} body={errorBody} variant="default" />
          ) : null}
          {state === 'error' && errorAction ? (
            <Inline gap="sm" sx={{ marginTop: s('sm'), justifyContent: 'center' }}>
              {errorAction}
            </Inline>
          ) : null}
          {state === 'ready' || state === 'loading' ? content : null}
        </Box>
      </Box>
    </Surface>
  );
};
